import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ValidationGroup, withCustomValidation } from '@bingads-webui-react/validation';
import { useWizardService } from '@bingads-webui-react/wizard';
import { KeywordInput } from '../keyword/keyword-input';
import {
  ADD_KEYWORDS_BY_ENTRY,
  ADD_KEYWORDS_BY_SELECTION,
} from '../keyword/keyword-methods';
import { KeywordSelector } from '../keyword/keyword-selector';
import { KeywordTextarea } from '../keyword/keyword-textarea';
import { MeasurementTypeCard } from '../measurement-type-card';
import { SearchCustomerId } from './search-customer-id';
import { SearchLiftLoading } from './search-lift-loading';

const SearchLiftInternal = ({
  customerId,
  isAllowed,
  isEnabled,
  keywordsMethod,
  onChangeCustomerId,
  onChangeKeywords,
  onChangeKeywordsMethod,
  onRemoveAllKeywords,
  onRemoveKeyword,
  onSelectKeywords,
  onToggle,
  selectedKeywords,
  showError,
  typedKeywordString,
  updateValidation,
}) => {
  const { state: wizardState } = useWizardService();
  const { wizardData } = wizardState.context;

  const i18n = useContext(I18nContext);
  const title = i18n.getString(_TL_('Search lift'));
  const description = i18n.getString(_TL_('Measure your brand search rate (search queries that contain your brand) by adding keywords associated with both your campaign and brand name. You can also enter your customer ID to help measure results. Note: If you use different customer IDs for your Audience and Search campaigns, please ensure that you enter the customer ID associated with your Search campaigns.'));
  const isLiftTypeDisabled = !isAllowed || !isEnabled;

  const hasNoKeywords = useMemo(() => {
    const keywordKeys = wizardData.fetchedKeywords
      ? Object.keys(wizardData.fetchedKeywords)
      : [];
    return keywordKeys.length === 0;
  }, [wizardData.fetchedKeywords]);

  const onValidateSearchLift = useCallback(({
    data,
    isDirty,
    isValid,
    messages,
  }) => {
    if (isValid) {
      updateValidation([], data);
    }

    if (isDirty) {
      updateValidation(messages, data);
    }
  }, [updateValidation]);

  const validationProps = {
    onValidate: onValidateSearchLift,
  };

  if (wizardData.isFetchingKeywords !== false) {
    return (
      <SearchLiftLoading title={title} />
    );
  }

  return wizardData.fetchedKeywords && (
    <ValidationGroup validationProps={validationProps}>
      <MeasurementTypeCard
        description={description}
        helpId="pop_BA_Experiments_SearchLift"
        isAllowed={isAllowed}
        isEnabled={isEnabled}
        onToggle={onToggle}
        title={title}
      >
        <KeywordInput
          entryControl={
            <KeywordTextarea
              isDisabled={isLiftTypeDisabled}
              keywordString={typedKeywordString}
              onChange={onChangeKeywords}
              showError={showError}
            />
          }
          isDisabled={isLiftTypeDisabled}
          isSelectDisabled={hasNoKeywords}
          keywordsMethod={keywordsMethod}
          onChangeKeywordsMethod={onChangeKeywordsMethod}
          selectControl={
            <KeywordSelector
              fetchedKeywords={wizardData.fetchedKeywords}
              isDisabled={isLiftTypeDisabled || hasNoKeywords}
              onRemove={onRemoveKeyword}
              onRemoveAll={onRemoveAllKeywords}
              onSelect={onSelectKeywords}
              orderedFetchedKeywords={wizardData.orderedFetchedKeywords}
              selected={selectedKeywords}
              showError={showError}
              useSearchLift={wizardData.useSearchLift}
            />
          }
        />
        <SearchCustomerId
          customerId={customerId}
          isDisabled={isLiftTypeDisabled}
          onChange={onChangeCustomerId}
          showError={showError}
        />
      </MeasurementTypeCard>
    </ValidationGroup>
  );
};

SearchLiftInternal.propTypes = {
  customerId: PropTypes.string.isRequired,
  isAllowed: PropTypes.bool.isRequired,
  isEnabled: PropTypes.bool,
  keywordsMethod: PropTypes.oneOf([
    ADD_KEYWORDS_BY_ENTRY,
    ADD_KEYWORDS_BY_SELECTION,
  ]),
  onChangeCustomerId: PropTypes.func.isRequired,
  onChangeKeywords: PropTypes.func.isRequired,
  onChangeKeywordsMethod: PropTypes.func.isRequired,
  onRemoveAllKeywords: PropTypes.func.isRequired,
  onRemoveKeyword: PropTypes.func.isRequired,
  onSelectKeywords: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  selectedKeywords: PropTypes.arrayOf(PropTypes.number).isRequired,
  showError: PropTypes.bool,
  typedKeywordString: PropTypes.string.isRequired,
  updateValidation: PropTypes.func.isRequired,
};

SearchLiftInternal.defaultProps = {
  isEnabled: true,
  keywordsMethod: null,
  showError: false,
};

export const SearchLift = withCustomValidation(SearchLiftInternal);
